import React, { useState, useEffect, useMemo } from 'react';
import { Card, Col, Row, Spin, message } from 'antd';
import ArticleCardSearchList from '../../componenents/Lists/ArticleCardSearchList';
import Container from '../../componenents/Container';
import FilterCard from '../../componenents/Cards/FilterCard';
import { useParams } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';

// Public vs. Authenticated searches
const SEARCH_PUBLIC_ARTICLES_QUERY = gql`
  query listPublicArticles($query: String!) {
    listPublicArticles(
      where: {
        OR: [
          { title: { contains: $query, mode: insensitive } }
          { tags: { contains: $query, mode: insensitive } }
          { Category: { is: { name: { contains: $query, mode: insensitive } } } }
        ]
      }
    ) {
      id
      title
      coverUrl
      tags
      likes { id }
      views
      createdAt
      Category { name }
      SubCategory { name }
    }
  }
`;

const SEARCH_ARTICLES_QUERY = gql`
  query listApprovedArticles($query: String!) {
    listApprovedArticles(
      where: {
        OR: [
          { title: { contains: $query, mode: insensitive } }
          { tags: { contains: $query, mode: insensitive } }
          { Category: { is: { name: { contains: $query, mode: insensitive } } } }
        ]
      }
    ) {
      id
      title
      coverUrl
      tags
      likes { id }
      User{ name }
      views
      createdAt
      Category { name }
      SubCategory { name }
    }
  }
`;

const UserArticleSearchPage = () => {
  const { q } = useParams();
  const [isAuth, setIsAuth] = useState(false);
  const [filters, setFilters] = useState({});

  // Determine auth status once
  useEffect(() => {
    setIsAuth(Boolean(localStorage.getItem('authToken')));
  }, []);

  // Choose query based on auth
  const gqlQuery = isAuth ? SEARCH_ARTICLES_QUERY : SEARCH_PUBLIC_ARTICLES_QUERY;

  const { loading, error, data } = useQuery(gqlQuery, {
    variables: { query: q },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (error) message.error('Failed to fetch articles');
  }, [error]);

  // Normalize list key
  const articlesData = useMemo(
    () => data?.listApprovedArticles || data?.listPublicArticles || [],
    [data]
  );

  // Client-side UI filtering
  const filteredArticles = useMemo(() => {
    return articlesData.filter(article => {
      if (filters.category && article.Category?.name !== filters.category) return false;
      if (filters.subcategory && article.SubCategory?.name !== filters.subcategory) return false;
      if (filters.tags?.length) {
        const tagsList = article.tags
          .split(',')
          .map(t => t.trim());
        if (!filters.tags.every(tag => tagsList.includes(tag))) return false;
      }
      if (filters.dateRange?.length === 2) {
        const [start, end] = filters.dateRange;
        const created = new Date(article.createdAt);
        if (created < start.toDate() || created > end.toDate()) return false;
      }
      return true;
    });
  }, [articlesData, filters]);

  const handleFilter = vals => setFilters(vals);

  if (loading && !data) {
    return (
      <Spin
        tip="Loading articles..."
        style={{ margin: '40px auto', display: 'block' }}
      />
    );
  }

  return (
    <div style={{ padding: 24 }}>
    <div
      style={{
        display: 'flex',
        gap: 32,                 // space between the two components
        alignItems: 'flex-start',
        flexWrap: 'wrap',       // allows stacking on narrow screens
      }}
    >
      {/* Sidebar / filter - fixed width on desktop, full width on small screens */}
      <div
        style={{
          flex: '0 0 280px',     // fixed width (can tweak: 240px, 300px...)
          minWidth: 240,         // prevents it from getting too narrow
        }}
      >
        <FilterCard
          categories={[
            ...new Set(
              articlesData.map(a => a.Category?.name).filter(Boolean)
            )
          ]}
          subcategories={[
            ...new Set(
              articlesData.map(a => a.SubCategory?.name).filter(Boolean)
            )
          ]}
          tags={[
            ...new Set(
              articlesData
                .flatMap(a => a.tags.split(',').map(tag => tag.trim()))
                .filter(Boolean)
            )
          ]}
          onFilter={handleFilter}
        />
      </div>
  
      {/* Main content - grows to fill remaining space */}
      <div
        style={{
          flex: '1 1 0px',       // grow and shrink, take remaining space
          minWidth: 320,         // ensures readability on narrow screens
        }}
      >
        <Card
          bordered={false}
          style={{
            borderRadius: 12,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}
        >
          <h2>Search Results for "{q}"</h2>
          {loading && <Spin tip="Updating results..." />}
          <ArticleCardSearchList articles={filteredArticles} />
        </Card>
      </div>
    </div>
  </div>
  
  );
};

export default UserArticleSearchPage;
